import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { fetchWithCSRF } from '../../utils/apiUtils';

// Async thunk to fetch an action list by ID
export const fetchActionListById = createAsyncThunk(
    'actionList/fetchActionListById',
    async (actionListId, { getState }) => {
        const response = await fetchWithCSRF(`${config.API_URI}/v1/actionLists/${actionListId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to fetch action list');
        }

        const data = await response.json();
        return { actionListId, actionList: data.actionList };
    }
);

// Async thunk to fetch cards by action list ID with pagination
export const fetchCardsByListId = createAsyncThunk(
    'actionList/fetchCardsByListId',
    async ({ actionListId, skip = 0, limit = 0, filterQuery = {} }) => {

        const queryParams = new URLSearchParams({
            skip: skip.toString(),
            limit: limit.toString(),
        });

        // Handle filterQuery parameters, especially arrays
        Object.entries(filterQuery).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                // Convert arrays to comma-separated strings
                queryParams.append(key, value.join(','));
            } else if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });


        const response = await fetch(`${config.API_URI}/v1/actionLists/${actionListId}/cards?${queryParams.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to fetch cards');
        }

        const data = await response.json();
        return {
            actionListId,
            cards: data.cards,
            hasMore: data.hasMore,
            totalCards: data.totalCards,
            isInitialLoad: skip === 0,
        };
    }
);


// Async thunk to batch-fetch cards for visible lists in one request
export const fetchBoardCards = createAsyncThunk(
    'actionList/fetchBoardCards',
    async ({ boardId, listIds = [], limit = 10, filterQuery = {} }) => {
        const queryParams = new URLSearchParams({
            limit: limit.toString(),
            listIds: listIds.join(','),
        });

        Object.entries(filterQuery).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                queryParams.append(key, value.join(','));
            } else if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const response = await fetch(`${config.API_URI}/v1/boards/${boardId}/cards?${queryParams.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to fetch board cards');
        }

        const data = await response.json();
        return {
            boardId,
            cardsByList: data.cardsByList,
            allListTotals: data.allListTotals || {},
        };
    }
);

const actionListSlice = createSlice({
    name: 'actionList',
    initialState: {
        actionLists: {}, // Stores action lists by ID
        status: 'idle',
        error: null,
    },
    reducers: {
        // Add a reducer to update totalCards when moving cards between lists
        updateTotalCardsOnMove: (state, action) => {
            const { sourceListId, targetListId } = action.payload;

            // Decrease count for source list
            if (state.actionLists[sourceListId] && state.actionLists[sourceListId].totalCards > 0) {
                state.actionLists[sourceListId].totalCards -= 1;
            }

            // Increase count for target list
            if (state.actionLists[targetListId]) {
                state.actionLists[targetListId].totalCards =
                    (state.actionLists[targetListId].totalCards || 0) + 1;
            }
        },
        // Increment total cards for a specific action list (optimistic add)
        incrementTotalCards: (state, action) => {
            const { actionListId } = action.payload;
            state.actionLists[actionListId] = state.actionLists[actionListId] || {
                details: null,
                cards: [],
                status: 'idle',
                error: null,
                hasMore: true,
                totalCards: 0
            };
            state.actionLists[actionListId].totalCards = (state.actionLists[actionListId].totalCards || 0) + 1;
        },
        // Decrement total cards for a specific action list (revert on failure)
        decrementTotalCards: (state, action) => {
            const { actionListId } = action.payload;
            if (!state.actionLists[actionListId]) return;
            state.actionLists[actionListId].totalCards = Math.max(0, (state.actionLists[actionListId].totalCards || 0) - 1);
        },
        // Clear all action list state (used when switching boards)
        resetActionLists: (state) => {
            state.actionLists = {};
            state.status = 'idle';
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch action list by ID
            .addCase(fetchActionListById.pending, (state, action) => {
                const actionListId = action.meta.arg;
                state.actionLists[actionListId] = state.actionLists[actionListId] || { details: null, cards: [], status: 'idle', error: null };
                state.actionLists[actionListId].status = 'loading';
            })
            .addCase(fetchActionListById.fulfilled, (state, action) => {
                const { actionListId, actionList } = action.payload;
                state.actionLists[actionListId] = state.actionLists[actionListId] || { details: null, cards: [], status: 'idle', error: null };
                state.actionLists[actionListId].details = actionList;
                state.actionLists[actionListId].status = 'succeeded';
            })
            .addCase(fetchActionListById.rejected, (state, action) => {
                const actionListId = action.meta.arg;
                state.actionLists[actionListId] = state.actionLists[actionListId] || { details: null, cards: [], status: 'idle', error: null };
                state.actionLists[actionListId].status = 'failed';
                state.actionLists[actionListId].error = action.error.message;
            })

            // Fetch cards by action list ID
            .addCase(fetchCardsByListId.pending, (state, action) => {
                const { actionListId } = action.meta.arg;
                state.actionLists[actionListId] = state.actionLists[actionListId] || {
                    details: null,
                    cards: [],
                    status: 'idle',
                    error: null,
                    hasMore: true,
                    totalCards: 0
                };
                state.actionLists[actionListId].status = 'loading';
            })
            .addCase(fetchCardsByListId.fulfilled, (state, action) => {
                const { actionListId, cards, hasMore, totalCards, isInitialLoad } = action.payload;
                state.actionLists[actionListId] = state.actionLists[actionListId] || {
                    details: null,
                    cards: [],
                    status: 'idle',
                    error: null,
                    hasMore: true,
                    totalCards: 0
                };

                // If this is the initial load, replace the cards array
                // Otherwise, append the new cards to the existing array
                if (isInitialLoad) {
                    state.actionLists[actionListId].cards = cards;
                } else {
                    state.actionLists[actionListId].cards = [
                        ...state.actionLists[actionListId].cards,
                        ...cards
                    ];
                }

                state.actionLists[actionListId].hasMore = hasMore;
                state.actionLists[actionListId].totalCards = totalCards;
                state.actionLists[actionListId].status = 'succeeded';
            })
            .addCase(fetchCardsByListId.rejected, (state, action) => {
                const { actionListId } = action.meta.arg;
                state.actionLists[actionListId] = state.actionLists[actionListId] || {
                    details: null,
                    cards: [],
                    status: 'idle',
                    error: null,
                    hasMore: true,
                    totalCards: 0
                };
                state.actionLists[actionListId].status = 'failed';
                state.actionLists[actionListId].error = action.error.message;
            })

            // Batch fetch all board cards
            .addCase(fetchBoardCards.fulfilled, (state, action) => {
                const { cardsByList, allListTotals } = action.payload;

                // Populate card data for visible (fetched) lists
                for (const [listId, listData] of Object.entries(cardsByList)) {
                    state.actionLists[listId] = {
                        details: state.actionLists[listId]?.details || null,
                        cards: listData.cards,
                        status: 'succeeded',
                        error: null,
                        hasMore: listData.hasMore,
                        totalCards: listData.totalCards,
                    };
                }

                // Set totalCards for ALL lists (including non-visible ones)
                if (allListTotals) {
                    for (const [listId, count] of Object.entries(allListTotals)) {
                        if (!cardsByList[listId]) {
                            state.actionLists[listId] = state.actionLists[listId] || {
                                details: null,
                                cards: [],
                                status: 'idle',
                                error: null,
                                hasMore: true,
                                totalCards: 0,
                            };
                            state.actionLists[listId].totalCards = count;
                        }
                    }
                }
            });
    },
});

// Export the action creators
export const { updateTotalCardsOnMove, incrementTotalCards, decrementTotalCards, resetActionLists } = actionListSlice.actions;

export default actionListSlice.reducer;
